import React, { useEffect, useState, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import API from "../../utils/api";
import ScoreRing from "../../components/ScoreRing";
import InstitutionContacts from "../../components/InstitutionContacts";
import { celebrate } from "../../components/CelebrationHub";
import TemplatePicker from "../../components/TemplatePicker";

// ── Activity type display config ────────────────────────────
const ACT = {
  visit:               { icon: "🏫", label: "Visit",              color: "#15803d", bg: "#dcfce7" },
  follow_up_completed: { icon: "✅", label: "Follow-up Done",     color: "#1d4ed8", bg: "#dbeafe" },
  follow_up_missed:    { icon: "⚠️",  label: "Missed Follow-up",  color: "#b45309", bg: "#fef3c7" },
  follow_up_scheduled: { icon: "📅", label: "Follow-up Booked",  color: "#4338ca", bg: "#e0e7ff" },
  stage_change:        { icon: "📈", label: "Stage Change",       color: "#7c3aed", bg: "#ede9fe" },
  note:                { icon: "📝", label: "Note",               color: "#475569", bg: "#f1f5f9" },
  call_log:            { icon: "📞", label: "Call Logged",        color: "#0e7490", bg: "#cffafe" },
  email_out:           { icon: "📧", label: "Email Sent",         color: "#c2410c", bg: "#ffedd5" },
  whatsapp_out:        { icon: "💬", label: "WhatsApp Sent",      color: "#15803d", bg: "#dcfce7" },
  whatsapp_in:         { icon: "💬", label: "WhatsApp Received",  color: "#0891b2", bg: "#e0f2fe" },
};

//  Pipeline stage config 
const PIPELINE = [
  { key: "visited",           label: "Visited",           icon: "", color: "#64748b", bg: "#f1f5f9" },
  { key: "interested",        label: "Interested",        icon: "", color: "#1d4ed8", bg: "#dbeafe" },
  { key: "proposal_sent",     label: "Proposal Sent",     icon: "", color: "#c2410c", bg: "#ffedd5" },
  { key: "mou_shared",        label: "MOU Shared",        icon: "", color: "#7c3aed", bg: "#ede9fe" },
  { key: "mou_signed",        label: "MOU Signed",        icon: "", color: "#4f46e5", bg: "#e0e7ff" },
  { key: "payment_requested", label: "Payment Requested", icon: "", color: "#b45309", bg: "#fef3c7" },
  { key: "closed_won",        label: "Closed  Won",      icon: "", color: "#15803d", bg: "#dcfce7" },
  { key: "closed_lost",       label: "Closed  Lost",     icon: "", color: "#dc2626", bg: "#fee2e2" },
];

const PIPELINE_MAP = Object.fromEntries(PIPELINE.map(p => [p.key, p]));

// Stages an employee can advance TO (not terminal ones directly via this flow)
const ADVANCEABLE = [
  { key: "interested",        label: "Mark Interested",   icon: "" },
  { key: "proposal_sent",     label: "Proposal Sent",     icon: "" },
  { key: "mou_shared",        label: "MOU Shared",        icon: "" },
  { key: "mou_signed",        label: "MOU Signed",        icon: "" },
  { key: "payment_requested", label: "Payment Requested", icon: "" },
  { key: "closed_won",        label: "Payment Received  Won!", icon: "" },
];

// Stages that come AFTER a given stage (to show valid next options)
const STAGE_ORDER = ["visited","interested","proposal_sent","mou_shared","mou_signed","payment_requested","closed_won","closed_lost"];

function nextStages(currentStage) {
  const idx = STAGE_ORDER.indexOf(currentStage);
  if (idx < 0) return ADVANCEABLE;
  return ADVANCEABLE.filter(a => STAGE_ORDER.indexOf(a.key) > idx);
}

//  Temperature config 
const TEMP = {
  hot:  { icon: "", label: "Hot",  color: "#dc2626", bg: "#fee2e2" },
  warm: { icon: "", label: "Warm", color: "#b45309", bg: "#fef9c3" },
  cold: { icon: "", label: "Cold", color: "#1d4ed8", bg: "#dbeafe" },
};

const CALL_OUTCOME_OPTIONS = [
  { key: "call_picked",     label: "Call Picked",              color: "#15803d", bg: "#dcfce7" },
  { key: "call_not_picked", label: "Call Not Picked",          color: "#b45309", bg: "#fef9c3" },
  { key: "call_back_later", label: "Asked to call back later", color: "#4338ca", bg: "#e0e7ff" },
  { key: "not_reachable",   label: "Not reachable",            color: "#475569", bg: "#f1f5f9" },
  { key: "disconnected",    label: "Disconnected the call",    color: "#dc2626", bg: "#fee2e2" },
  { key: "others",          label: "Others",                   color: "#64748b", bg: "#f8fafc" },
];

const EMAIL_STATUS_OPTIONS = [
  { key: "sent_proposal", label: "Sent Proposal" },
  { key: "sent_mou", label: "Sent MOU" },
  { key: "sent_follow_up", label: "Sent Follow-Up" },
  { key: "others", label: "Others" },
];

//  Filter tab definitions 
const FILTER_TABS = [
  { key: "all",               label: "All" },
  { key: "visited",           label: "Visited" },
  { key: "interested",        label: "Interested" },
  { key: "proposal_sent",     label: "Proposal" },
  { key: "mou_shared",        label: "MOU Shared" },
  { key: "mou_signed",        label: "MOU Signed" },
  { key: "payment_requested", label: "Payment" },
  { key: "closed_won",        label: "Won " },
];

const LOST_REASONS = [
  ["budget_unavailable", "Budget unavailable"],
  ["not_interested", "Not interested"],
  ["competitor_selected", "Competitor selected"],
  ["no_management_approval", "No management approval"],
  ["product_mismatch", "Product mismatch"],
  ["timing_postponed", "Timing postponed"],
  ["unable_to_contact", "Unable to contact"],
  ["duplicate_institution", "Duplicate institution"],
  ["other", "Other"],
];

//  Helpers 
function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

//  Main component 
export default function MyLeads({ accent }) {
  const [leads, setLeads]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState("all");
  const [showClosed, setShowClosed] = useState(false);
  const [search, setSearch]         = useState("");
  const [focus, setFocus]           = useState("all");
  const [sortBy, setSortBy]         = useState("priority");

  // Modal state
  const [modal, setModal]           = useState(null); // null | lead object
  const [view, setView]             = useState("advance"); // "advance" | "close"

  // Advance stage form
  const [targetStage, setTargetStage] = useState("");
  const [dealValue, setDealValue]     = useState("");
  const [stageNotes, setStageNotes]   = useState("");
  const [nextAction, setNextAction]   = useState("");
  const [expectedCloseDate, setExpectedCloseDate] = useState("");
  const [advancing, setAdvancing]     = useState(false);

  // Close form
  const [closedReason, setClosedReason] = useState("");
  const [lostReasonCategory, setLostReasonCategory] = useState("");
  const [competitorName, setCompetitorName] = useState("");
  const [closing, setClosing]           = useState(false);
  const [reactivateAction, setReactivateAction] = useState("");
  const [reactivateDate, setReactivateDate] = useState("");
  const [reactivating, setReactivating] = useState(false);

  // Notes / temp update
  const [tempUpdate, setTempUpdate]   = useState("");
  const [notesUpdate, setNotesUpdate] = useState("");
  const [saving, setSaving]           = useState(false);

  // Activity timeline
  const [activities, setActivities]           = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [logType, setLogType]                 = useState("note");
  const [logTitle, setLogTitle]               = useState("");
  const [logBody, setLogBody]                 = useState("");
  const [logging, setLogging]                 = useState(false);

  // WhatsApp send state (Business API — from Timeline tab)
  const [waMessage, setWaMessage]             = useState("");
  const [waSending, setWaSending]             = useState(false);

  // ── Click-to-Call dialog state ────────────────────────────
  const [callDialog, setCallDialog]           = useState(null); // lead object | null
  const [callOutcome, setCallOutcome]         = useState("call_picked");
  const [callDuration, setCallDuration]       = useState("");
  const [callNotes, setCallNotes]             = useState("");
  const [loggingCall, setLoggingCall]         = useState(false);

  // ── Personal WhatsApp log dialog state ───────────────────
  const [waLogDialog, setWaLogDialog]         = useState(null); // lead object | null
  const [waLogMessage, setWaLogMessage]       = useState("");
  const [loggingWa, setLoggingWa]             = useState(false);

  // ── Email compose dialog state ────────────────────────────
  const [emailDialog, setEmailDialog]         = useState(null); // lead object | null
  const [emailTo, setEmailTo]                 = useState("");
  const [emailSubject, setEmailSubject]       = useState("");
  const [emailBody, setEmailBody]             = useState("");
  const [emailStatus, setEmailStatus]         = useState("sent_follow_up");
  const [emailStatusOther, setEmailStatusOther] = useState("");
  const [sendingEmail, setSendingEmail]       = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(null); // "email" | "whatsapp" | null

  const templateLead = showTemplatePicker === "email" ? emailDialog : waLogDialog;
  let templateSender = "Our Team";
  let templateCompany = "Ebek";
  try {
    const token = localStorage.getItem("crm_token") || "";
    if (token) {
      const payload = JSON.parse(atob(token.split(".")[1]));
      templateSender = payload?.name || templateSender;
      templateCompany = payload?.company === "ebekmed" ? "EbekMed" : "Ebek";
    }
  } catch { /* use safe defaults */ }

  //  Fetch leads (with deal scores)
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      if (showClosed) {
        // closed leads don't need scoring
        const r = await API.get("/leads/mine", { params: { include_closed: "true" } });
        setLeads(r.data);
      } else {
        const r = await API.get("/leads/mine/scored");
        setLeads(r.data);
      }
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [showClosed]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  //  Filter 
  const filtered = useMemo(() => {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    const query = search.trim().toLowerCase();
    const hasFlag = (lead, text) => Array.isArray(lead.deal_flags)
      && lead.deal_flags.some(flag => String(flag).includes(text));

    const result = leads.filter(lead => {
      if (filter !== "all" && lead.pipeline_stage !== filter) return false;
      if (query && ![lead.institution_name, lead.contact_name, lead.contact_phone, lead.contact_email]
        .some(value => String(value || "").toLowerCase().includes(query))) return false;
      if (focus === "overdue" && !(lead.next_follow_up && String(lead.next_follow_up).slice(0, 10) < today)) return false;
      if (focus === "no_followup" && lead.next_follow_up) return false;
      if (focus === "stuck" && !hasFlag(lead, "Stage Stuck")) return false;
      if (focus === "high_value" && Math.max(Number(lead.deal_value) || 0, Number(lead.approx_revenue) || 0) < 100000) return false;
      return true;
    });

    return result.sort((a, b) => {
      if (sortBy === "value") return Math.max(Number(b.deal_value) || 0, Number(b.approx_revenue) || 0) - Math.max(Number(a.deal_value) || 0, Number(a.approx_revenue) || 0);
      if (sortBy === "followup") return String(a.next_follow_up || "9999-12-31").localeCompare(String(b.next_follow_up || "9999-12-31"));
      if (sortBy === "recent") return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
      return (Number(b.deal_score) || 0) - (Number(a.deal_score) || 0);
    });
  }, [leads, filter, search, focus, sortBy]);

  const countFor = (key) => {
    if (key === "all") return leads.length;
    return leads.filter(l => l.pipeline_stage === key).length;
  };

  //  Open modal
  function openModal(lead) {
    setModal(lead);
    setView(lead.pipeline_stage === "closed_lost" ? "reactivate" : "timeline");
    setTargetStage("");
    setDealValue(lead.deal_value || "");
    setStageNotes("");
    setNextAction(lead.next_action || "");
    setExpectedCloseDate(lead.expected_close_date ? String(lead.expected_close_date).slice(0, 10) : "");
    setClosedReason("");
    setLostReasonCategory("");
    setCompetitorName("");
    setReactivateAction("");
    setReactivateDate("");
    setTempUpdate(lead.lead_status || "warm");
    setNotesUpdate(lead.notes || "");
    setActivities([]);
    setLogType("note");
    setLogTitle("");
    setLogBody("");
    fetchActivities(lead.id);
  }

  function closeModal() {
    setModal(null);
  }

  //  Advance stage 
  async function handleAdvance() {
    if (!targetStage) return toast.error("Select a stage to advance to");
    if (!targetStage.startsWith("closed_") && !nextAction.trim()) return toast.error("Add the next action for this lead");
    if (["proposal_sent", "mou_shared", "mou_signed", "payment_requested"].includes(targetStage) && !expectedCloseDate) return toast.error("Add an expected close date");
    setAdvancing(true);
    try {
      const body = {
        to_stage: targetStage,
        notes: stageNotes || undefined,
        next_action: nextAction.trim() || undefined,
        expected_close_date: expectedCloseDate || undefined,
      };
      if (dealValue) body.deal_value = parseFloat(dealValue);

      const res = await API.put(`/leads/${modal.id}/stage`, body);
      setLeads(l => l.map(x => x.id === res.data.id ? { ...x, ...res.data } : x));
      const stageCfg = PIPELINE_MAP[targetStage];
      toast.success(`${stageCfg?.icon || ""} Advanced to ${stageCfg?.label || targetStage}`);
      if (targetStage === "closed_won") celebrate("dealWon", { message: `${modal.institution_name || "Deal"} successfully converted` });
      else if (targetStage === "mou_signed") celebrate("mouSigned", { message: `${modal.institution_name || "Institution"} signed the MOU` });
      closeModal();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to advance stage");
    } finally {
      setAdvancing(false);
    }
  }

  //  Close as Not Interested 
  async function handleClose() {
    if (!lostReasonCategory) return toast.error("Select a lost reason");
    if (!closedReason.trim()) return toast.error("Add remarks for closing this lead");
    if (lostReasonCategory === "competitor_selected" && !competitorName.trim()) return toast.error("Enter the competitor name");
    setClosing(true);
    try {
      await API.put(`/leads/${modal.id}/close-lost`, { closed_reason: closedReason, lost_reason_category: lostReasonCategory, competitor_name: competitorName.trim() || null });
      // Remove from active list or refresh
      await fetchLeads();
      toast.success("Lead closed as Not Interested");
      closeModal();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to close lead");
    } finally {
      setClosing(false);
    }
  }

  async function handleReactivate() {
    if (!reactivateAction.trim()) return toast.error("Add the next action");
    if (!reactivateDate) return toast.error("Select the next follow-up date");
    setReactivating(true);
    try {
      await API.put(`/leads/${modal.id}/reactivate`, { next_action: reactivateAction.trim(), next_follow_up: reactivateDate, to_stage: "visited" });
      await fetchLeads();
      toast.success("Lead reactivated and follow-up scheduled");
      closeModal();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to reactivate lead");
    } finally {
      setReactivating(false);
    }
  }

  async function completeManagerAction(event, lead) {
    event.stopPropagation();
    try {
      await API.put(`/leads/manager-actions/${lead.manager_action_id}`, { status:"completed", completion_notes:"Completed by employee from lead list" });
      toast.success("Manager action marked complete");
      await fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not complete action");
    }
  }

  //  Update temperature / notes 
  async function handleSaveNotes() {
    setSaving(true);
    try {
      const res = await API.put(`/leads/${modal.id}`, {
        lead_status: tempUpdate || undefined,
        notes: notesUpdate || undefined,
      });
      setLeads(l => l.map(x => x.id === res.data.id ? { ...x, ...res.data } : x));
      if (tempUpdate === "hot" && modal.lead_status !== "hot") celebrate("hotLead", { message: `${modal.institution_name || "Lead"} is now a hot lead` });
      toast.success("Lead updated—nice one ✨");
      closeModal();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  }

  // ── Activity timeline helpers ─────────────────────────────
  async function fetchActivities(leadId) {
    setActivitiesLoading(true);
    try {
      const r = await API.get(`/activities/lead/${leadId}`);
      setActivities(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      // 404 = lead exists but no activities yet — show empty silently
      if (err?.response?.status !== 404) {
        toast.error("Could not load timeline");
      }
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }

  async function handleLogActivity() {
    if (!logTitle.trim()) return toast.error("Please describe the activity");
    setLogging(true);
    try {
      await API.post(`/activities/lead/${modal.id}`, {
        type: logType,
        title: logTitle.trim(),
        body: logBody || undefined,
      });
      setLogTitle("");
      setLogBody("");
      await fetchActivities(modal.id);
      toast.success("Activity logged ✓");
    } catch {
      toast.error("Failed to log activity");
    } finally {
      setLogging(false);
    }
  }

  // ── Send WhatsApp message ─────────────────────────────────
  async function handleSendWhatsApp() {
    if (!waMessage.trim()) return toast.error("Type a message to send");
    if (!modal.contact_phone) return toast.error("No phone number for this lead");
    setWaSending(true);
    try {
      await API.post("/whatsapp/send", {
        lead_id: modal.id,
        phone: modal.contact_phone,
        message: waMessage.trim(),
      });
      setWaMessage("");
      await fetchActivities(modal.id);
      toast.success("WhatsApp sent ✓");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to send WhatsApp");
    } finally {
      setWaSending(false);
    }
  }

  // ── Click-to-Call handlers ────────────────────────────────
  function handleCallButton(e, lead) {
    e.stopPropagation();
    if (!lead.contact_phone) return toast.error("No phone number saved for this lead");
    // Open the phone dialer on mobile
    window.location.href = `tel:${lead.contact_phone}`;
    // After brief delay, show the call-log dialog
    setTimeout(() => {
      setCallDialog(lead);
      setCallOutcome("call_picked");
      setCallDuration("");
      setCallNotes("");
    }, 600);
  }

  async function handleLogCall() {
    setLoggingCall(true);
    try {
      const outcomeLabel = CALL_OUTCOME_OPTIONS.find(item => item.key === callOutcome)?.label || callOutcome;

      const title = callOutcome === "call_picked"
        ? `Call with ${callDialog.contact_name || callDialog.institution_name}${callDuration ? ` · ${callDuration} min` : ""}`
        : `Call to ${callDialog.contact_name || callDialog.institution_name} — ${outcomeLabel}`;

      await API.post(`/activities/lead/${callDialog.id}`, {
        type:     "call_log",
        title,
        body:     callNotes || undefined,
        metadata: { outcome: callOutcome, duration_min: callDuration ? Number(callDuration) : null },
      });

      toast.success("Call logged ✓");
      setCallDialog(null);
      // Refresh timeline if the modal for this lead is open
      if (modal?.id === callDialog.id) fetchActivities(callDialog.id);
    } catch {
      toast.error("Failed to log call");
    } finally {
      setLoggingCall(false);
    }
  }

  // ── Personal WhatsApp deep-link handlers ─────────────────
  function handleWhatsAppButton(e, lead) {
    e.stopPropagation();
    if (!lead.contact_phone) return toast.error("No phone number saved for this lead");
    const clean = lead.contact_phone.replace(/\D/g, "");
    const to    = clean.startsWith("91") ? clean : `91${clean}`;
    const greeting = encodeURIComponent(
      `Hi ${lead.contact_name ? lead.contact_name.split(" ")[0] : ""},`
    );
    window.open(`https://wa.me/${to}?text=${greeting}`, "_blank");
    // Show log dialog so rep can record what they sent
    setWaLogDialog(lead);
    setWaLogMessage("");
  }

  async function handleLogWaMessage() {
    if (!waLogMessage.trim()) return toast.error("Describe the message you sent");
    setLoggingWa(true);
    try {
      await API.post(`/activities/lead/${waLogDialog.id}`, {
        type:  "whatsapp_out",
        title: `WhatsApp to ${waLogDialog.contact_name || waLogDialog.institution_name}`,
        body:  waLogMessage.trim(),
        metadata: { channel: "personal_whatsapp" },
      });
      toast.success("WhatsApp logged ✓");
      setWaLogDialog(null);
      if (modal?.id === waLogDialog.id) fetchActivities(waLogDialog.id);
    } catch {
      toast.error("Failed to log WhatsApp");
    } finally {
      setLoggingWa(false);
    }
  }

  // ── Email compose & send handlers ─────────────────────────
  function handleEmailButton(e, lead) {
    e.stopPropagation();
    setEmailDialog(lead);
    setEmailTo(lead.contact_email || "");
    setEmailSubject(`Regarding ${lead.institution_name}`);
    setEmailStatus("sent_follow_up");
    setEmailStatusOther("");
    // Try to get name from stored auth token payload
    let senderName = "Our Team";
    try {
      const token = localStorage.getItem("crm_token") || "";
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload?.name) senderName = payload.name;
      }
    } catch { /* ignore */ }
    setEmailBody(
      `Dear ${lead.contact_name ? lead.contact_name.split(" ")[0] : "Sir/Madam"},\n\n` +
      `\n\nWarm regards,\n${senderName}`
    );
  }

  async function handleSendEmail() {
    if (!emailTo.trim())      return toast.error("Enter recipient email address");
    if (!emailSubject.trim()) return toast.error("Enter email subject");
    if (!emailBody.trim())    return toast.error("Enter email message");
    setSendingEmail(true);
    try {
      await API.post("/email/send", {
        lead_id: emailDialog.id,
        to:      emailTo.trim(),
        subject: emailSubject.trim(),
        body:    emailBody.trim(),
        status_update: emailStatus === "others" ? emailStatusOther.trim() : EMAIL_STATUS_OPTIONS.find(item => item.key === emailStatus)?.label,
      });
      toast.success("Email sent and logged ✓");
      setEmailDialog(null);
      if (modal?.id === emailDialog.id) fetchActivities(emailDialog.id);
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to send email";
      if (msg.includes("not configured")) {
        toast.error("Email SMTP not set up yet. Ask your admin to configure it.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSendingEmail(false);
    }
  }

  //  Render
  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b" }}> My Leads</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>Sales pipeline tracker</div>
        </div>
        <button onClick={() => setShowClosed(v => !v)}
          style={{ padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: showClosed ? "#1e293b" : "#fff", color: showClosed ? "#fff" : "#64748b", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          {showClosed ? "Hide Lost" : "Show Lost"}
        </button>
      </div>

      {/* Search and priority controls */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 8, marginTop: 14 }}>
        <input
          type="search"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Search institution, contact, phone or email"
          aria-label="Search leads"
          style={{ width: "100%", minWidth: 0, padding: "10px 12px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontFamily: "inherit", fontSize: 12, outline: "none" }}
        />
        <select
          value={sortBy}
          onChange={event => setSortBy(event.target.value)}
          aria-label="Sort leads"
          style={{ maxWidth: 130, padding: "10px 8px", borderRadius: 10, border: "1.5px solid #e2e8f0", background: "#fff", fontFamily: "inherit", fontSize: 11, fontWeight: 700 }}
        >
          <option value="priority">Best score</option>
          <option value="followup">Next follow-up</option>
          <option value="value">Highest value</option>
          <option value="recent">Recently updated</option>
        </select>
      </div>

      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, marginTop: 8 }}>
        {[
          ["all", "All priorities"],
          ["overdue", "Overdue"],
          ["no_followup", "No follow-up"],
          ["stuck", "Stage stuck"],
          ["high_value", "High value"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFocus(key)}
            style={{ padding: "7px 10px", borderRadius: 8, border: `1px solid ${focus === key ? accent : "#e2e8f0"}`, background: focus === key ? `${accent}14` : "#fff", color: focus === key ? accent : "#64748b", fontFamily: "inherit", fontSize: 10, fontWeight: 800, whiteSpace: "nowrap", flexShrink: 0, cursor: "pointer" }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4, marginTop: 10 }}>
        {FILTER_TABS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{ padding: "7px 12px", borderRadius: 100, border: "none", fontFamily: "inherit", fontWeight: 700, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
              background: filter === f.key ? accent : "#f1f5f9",
              color: filter === f.key ? "#fff" : "#64748b" }}>
            {f.label} {countFor(f.key) > 0 ? `(${countFor(f.key)})` : ""}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && <div style={{ textAlign: "center", color: "#94a3b8", padding: 40 }}>Loading</div>}

      {/* Empty */}
      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}></div>
          <div style={{ fontWeight: 600 }}>No leads found</div>
          <div style={{ fontSize: 13 }}>Log visits to create leads in your pipeline</div>
        </div>
      )}

      {/* Lead cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map(l => {
          const stage = PIPELINE_MAP[l.pipeline_stage] || PIPELINE_MAP["visited"];
          const temp  = TEMP[l.lead_status];
          const isClosed = l.pipeline_stage === "closed_lost";

          return (
            <div key={l.id} onClick={() => openModal(l)}
              style={{ background: "#fff", border: `1.5px solid ${isClosed ? "#fecaca" : "#e2e8f0"}`, borderRadius: 16, padding: 16,
                cursor: "pointer", opacity: isClosed ? 0.82 : 1,
                transition: "box-shadow 0.2s" }}
              onMouseEnter={e => { if (!isClosed) e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; }}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>

              {/* Top row */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 2 }}>{l.institution_name}</div>
                  {l.contact_name && <div style={{ fontSize: 12, color: "#64748b" }}> {l.contact_name}</div>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, marginLeft: 8, flexShrink: 0 }}>
                  {/* Pipeline stage badge */}
                  <div style={{ background: stage.bg, color: stage.color, borderRadius: 100, padding: "4px 10px", fontSize: 11, fontWeight: 700 }}>
                    {stage.icon} {stage.label}
                  </div>
                  {/* Deal score ring */}
                  {l.deal_score != null && (
                    <ScoreRing score={l.deal_score} grade={l.deal_grade || "F"} size={48} />
                  )}
                </div>
              </div>

              {/* Chips row */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                {/* Temperature */}
                {temp && (
                  <span style={{ background: temp.bg, color: temp.color, borderRadius: 6, padding: "3px 8px", fontSize: 11, fontWeight: 700 }}>
                    {temp.icon} {temp.label}
                  </span>
                )}

                {/* Follow-up counts */}
                {(parseInt(l.total_followups) > 0) && (
                  <span style={{ background: "#f1f5f9", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#64748b" }}>
                     {l.completed_followups}/{l.total_followups} visits
                  </span>
                )}

                {/* Next follow-up */}
                {l.next_follow_up && (
                  <span style={{ background: "#fef9c3", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#92400e" }}>
                     {fmtDate(l.next_follow_up)}
                  </span>
                )}

                {/* Deal value */}
                {l.deal_value && (
                  <span style={{ background: "#dcfce7", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#15803d" }}>
                    Rs. {parseFloat(l.deal_value).toLocaleString("en-IN")}
                  </span>
                )}

                {l.approx_revenue && (
                  <span style={{ background: "#ecfdf5", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#047857" }}>
                    Est. Rs. {parseFloat(l.approx_revenue).toLocaleString("en-IN")}
                  </span>
                )}

                {l.next_action && (
                  <span style={{ background: "#eef2ff", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#4338ca" }}>
                    Next: {l.next_action}
                  </span>
                )}

                {l.expected_close_date && (
                  <span style={{ background: "#f5f3ff", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#6d28d9" }}>
                    Close by {fmtDate(l.expected_close_date)}
                  </span>
                )}

                {/* Closed reason */}
                {isClosed && l.closed_reason && (
                  <span style={{ background: "#fee2e2", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#dc2626" }}>
                    Reason: {l.closed_reason}
                  </span>
                )}
              </div>

              {/* Deal flags / alerts row */}
              {l.manager_action_id && (
                <div style={{ marginTop:9, padding:"9px 10px", borderRadius:9, background:"#eff6ff", border:"1px solid #bfdbfe", color:"#1e40af", fontSize:10 }}>
                  <div style={{ fontWeight:800 }}>Manager action: {l.manager_action_text}</div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, marginTop:5 }}>
                    <span>Due {fmtDate(l.manager_action_due)}</span>
                    <button onClick={event => completeManagerAction(event,l)} style={{ border:0, borderRadius:6, padding:"5px 8px", background:"#1d4ed8", color:"#fff", fontSize:9, fontWeight:800, cursor:"pointer" }}>Mark Complete</button>
                  </div>
                </div>
              )}
              {l.deal_flags && l.deal_flags.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                  {l.deal_flags.map((flag, fi) => (
                    <span key={fi} style={{ background: "#fefce8", border: "1px solid #fde047", color: "#713f12", borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700 }}>
                      {flag}
                    </span>
                  ))}
                </div>
              )}

              {/* Key dates */}
              {(l.proposal_sent_at || l.mou_shared_at || l.mou_signed_at || l.payment_requested_at || l.payment_received_at) && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f5f9", display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {l.proposal_sent_at && (
                    <span style={{ fontSize: 10, color: "#94a3b8" }}> Proposal: {fmtDate(l.proposal_sent_at)}</span>
                  )}
                  {l.mou_shared_at && (
                    <span style={{ fontSize: 10, color: "#94a3b8" }}> MOU Shared: {fmtDate(l.mou_shared_at)}</span>
                  )}
                  {l.mou_signed_at && (
                    <span style={{ fontSize: 10, color: "#94a3b8" }}> MOU Signed: {fmtDate(l.mou_signed_at)}</span>
                  )}
                  {l.payment_requested_at && (
                    <span style={{ fontSize: 10, color: "#94a3b8" }}> Payment Req: {fmtDate(l.payment_requested_at)}</span>
                  )}
                  {l.payment_received_at && (
                    <span style={{ fontSize: 10, color: "#15803d", fontWeight: 700 }}> Won: {fmtDate(l.payment_received_at)}</span>
                  )}
                </div>
              )}

              {/* Quick action buttons — Call / WhatsApp / Email */}
              {!isClosed && (
                <div style={{ display: "flex", gap: 6, marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f5f9" }}
                  onClick={e => e.stopPropagation()}>
                    <button onClick={e => handleCallButton(e, l)} disabled={!l.contact_phone}
                      title={l.contact_phone ? "Call this contact" : "Add a phone number to enable calling"}
                      style={{ flex: 1, padding: "8px 4px", borderRadius: 10, border: "1.5px solid #bbf7d0",
                        background: l.contact_phone ? "#f0fdf4" : "#f8fafc", color: l.contact_phone ? "#15803d" : "#94a3b8", fontFamily: "inherit",
                        fontWeight: 700, fontSize: 12, cursor: l.contact_phone ? "pointer" : "not-allowed", display: "flex",
                        alignItems: "center", justifyContent: "center", gap: 4 }}>
                      📞 Call
                    </button>
                    <button onClick={e => handleWhatsAppButton(e, l)} disabled={!l.contact_phone}
                      title={l.contact_phone ? "Open WhatsApp or use an Ebekmed template" : "Add a phone number to enable WhatsApp"}
                      style={{ flex: 1, padding: "8px 4px", borderRadius: 10, border: "1.5px solid #86efac",
                        background: l.contact_phone ? "#dcfce7" : "#f8fafc", color: l.contact_phone ? "#166534" : "#94a3b8", fontFamily: "inherit",
                        fontWeight: 700, fontSize: 12, cursor: l.contact_phone ? "pointer" : "not-allowed", display: "flex",
                        alignItems: "center", justifyContent: "center", gap: 4 }}>
                      💬 WhatsApp
                    </button>
                    <button onClick={e => handleEmailButton(e, l)} disabled={!l.contact_email}
                      title={l.contact_email ? "Compose an email or use an Ebekmed template" : "Add an email address to enable email"}
                      style={{ flex: 1, padding: "8px 4px", borderRadius: 10, border: "1.5px solid #fed7aa",
                        background: l.contact_email ? "#fff7ed" : "#f8fafc", color: l.contact_email ? "#c2410c" : "#94a3b8", fontFamily: "inherit",
                        fontWeight: 700, fontSize: 12, cursor: l.contact_email ? "pointer" : "not-allowed", display: "flex",
                        alignItems: "center", justifyContent: "center", gap: 4 }}>
                      📧 Email
                    </button>
                </div>
              )}

              {/* Tap hint */}
              {!isClosed ? (
                <div style={{ marginTop: 8, fontSize: 11, color: "#94a3b8", textAlign: "right", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
                  <span style={{ background: "#f1f5f9", borderRadius: 6, padding: "2px 8px", fontSize: 10, color: "#94a3b8" }}>📋 Timeline</span>
                  <span>Tap to open </span>
                </div>
              ) : (
                <div style={{ marginTop:8, fontSize:11, color:"#15803d", textAlign:"right", fontWeight:800 }}>Tap to reactivate</div>
              )}
            </div>
          );
        })}
      </div>

      {/*  Modal  */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "flex-end" }}
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>

            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b", marginBottom: 2 }}>{modal.institution_name}</div>
                {modal.contact_name && <div style={{ fontSize: 12, color: "#64748b" }}> {modal.contact_name}</div>}
              </div>
              <button onClick={closeModal} style={{ background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16 }}></button>
            </div>

            {/* Current stage banner */}
            {(() => {
              const s = PIPELINE_MAP[modal.pipeline_stage] || PIPELINE_MAP["visited"];
              return (
                <div style={{ background: s.bg, borderRadius: 12, padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 20 }}>{s.icon}</span>
                  <div>
                    <div style={{ fontSize: 11, color: s.color, fontWeight: 700, textTransform: "uppercase" }}>Current Stage</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: s.color }}>{s.label}</div>
                  </div>
                </div>
              );
            })()}

            {/* Tab switch */}
            <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 12, padding: 4, marginBottom: 20, gap: 3, overflowX: "auto" }}>
              {(modal.pipeline_stage === "closed_lost" ? [
                { key: "reactivate", label: "Reactivate Lead" },
              ] : [
                { key: "timeline", label: "📋 Timeline" },
                { key: "contacts", label: "👥 People" },
                { key: "score",    label: "🎯 Score" },
                { key: "advance",  label: "🚀 Advance" },
                { key: "notes",    label: "📝 Notes" },
                { key: "close",    label: "✖ Close" },
              ]).map(t => (
                <button key={t.key} onClick={() => setView(t.key)}
                  style={{ flex: 1, padding: "9px 6px", borderRadius: 10, border: "none", fontFamily: "inherit", fontWeight: 700, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                    background: view === t.key ? (t.key === "close" ? "#dc2626" : "#fff") : "transparent",
                    color: view === t.key ? (t.key === "close" ? "#fff" : "#1e293b") : "#64748b",
                    boxShadow: view === t.key ? "0 1px 4px rgba(0,0,0,0.1)" : "none" }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* ── SCORE VIEW ── */}
            {view === "score" && (() => {
              const sig = modal.deal_signals || {};
              const flags = modal.deal_flags || [];
              const score = modal.deal_score;
              const grade = modal.deal_grade;

              const SIGNAL_ROWS = [
                { label: "Pipeline Stage",   key: "stage_score",       max: 30, icon: "📈" },
                { label: "Temperature",      key: "temperature_score", max: 20, icon: "🌡" },
                { label: "Follow-up Rate",   key: "followup_score",    max: 20, icon: "✅" },
                { label: "Recency",          key: "recency_score",     max: 15, icon: "🕐" },
                { label: "Deal Value",       key: "value_score",       max: 10, icon: "💰" },
                { label: "Contact Info",     key: "contact_score",     max: 5,  icon: "📋" },
              ];

              return (
                <div>
                  {/* Big ring + overall */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 0 24px" }}>
                    {score != null ? (
                      <>
                        <ScoreRing score={score} grade={grade || "F"} size={100} />
                        <div style={{ marginTop: 12, fontSize: 13, fontWeight: 700, color: "#475569" }}>
                          Deal Health Score
                        </div>
                        {modal.score_updated_at && (
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                            Last scored: {new Date(modal.score_updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ color: "#94a3b8", textAlign: "center", padding: 20 }}>
                        <div style={{ fontSize: 36, marginBottom: 8 }}>⏳</div>
                        <div style={{ fontWeight: 600 }}>Score not yet computed</div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>Runs nightly at 2 AM</div>
                      </div>
                    )}
                  </div>

                  {/* Signal breakdown bars */}
                  {score != null && (
                    <div style={{ background: "#f8fafc", borderRadius: 14, padding: 16, marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Score Breakdown
                      </div>
                      {SIGNAL_ROWS.map(row => {
                        const val = sig[row.key] ?? 0;
                        const pct = Math.round((val / row.max) * 100);
                        return (
                          <div key={row.key} style={{ marginBottom: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                              <span style={{ fontSize: 12, color: "#475569" }}>{row.icon} {row.label}</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{val}/{row.max}</span>
                            </div>
                            <div style={{ background: "#e2e8f0", borderRadius: 100, height: 6 }}>
                              <div style={{
                                height: 6, borderRadius: 100,
                                width: `${pct}%`,
                                background: pct >= 70 ? "#16a34a" : pct >= 40 ? "#f59e0b" : "#ef4444",
                                transition: "width 0.4s ease",
                              }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Flags / alerts */}
                  {flags.length > 0 && (
                    <div style={{ background: "#fefce8", border: "1.5px solid #fde047", borderRadius: 14, padding: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#713f12", marginBottom: 8 }}>⚠️ Active Alerts</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {flags.map((flag, i) => (
                          <span key={i} style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#c2410c", borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 700 }}>
                            {flag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {flags.length === 0 && score != null && (
                    <div style={{ textAlign: "center", padding: "12px 0", color: "#16a34a", fontWeight: 600, fontSize: 13 }}>
                      ✅ No alerts — this lead is on track!
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── TIMELINE VIEW ── */}
            {/* Everyone we know at this school, so the next visit is not
                spent asking the desk who to speak to. */}
            {view === "contacts" && (
              <InstitutionContacts
                institutionId={modal.institution_id}
                institutionName={modal.institution_name}
                accent={accent}
              />
            )}

            {view === "timeline" && (
              <div>
                {/* Quick log bar */}
                <div style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 14, padding: 14, marginBottom: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", marginBottom: 10 }}>
                    Log Activity
                  </div>
                  {/* Type selector */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                    {[
                      { key: "note",         icon: "📝", label: "Note" },
                      { key: "call_log",     icon: "📞", label: "Call" },
                      { key: "email_out",    icon: "📧", label: "Email" },
                      { key: "whatsapp_out", icon: "💬", label: "WhatsApp" },
                    ].map(t => (
                      <button key={t.key} onClick={() => { setLogType(t.key); setWaMessage(""); }}
                        style={{ padding: "6px 10px", borderRadius: 8, border: `2px solid ${logType === t.key ? (ACT[t.key]?.color || "#1e293b") : "#e2e8f0"}`,
                          background: logType === t.key ? (ACT[t.key]?.bg || "#f1f5f9") : "#fff",
                          cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 11,
                          color: logType === t.key ? (ACT[t.key]?.color || "#1e293b") : "#94a3b8" }}>
                        {t.icon} {t.label}
                      </button>
                    ))}
                  </div>

                  {/* WhatsApp: live send panel */}
                  {logType === "whatsapp_out" ? (
                    <div>
                      {modal.contact_phone ? (
                        <>
                          <div style={{ fontSize: 11, color: "#15803d", fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                            <span>💬</span> Sending to {modal.contact_name ? `${modal.contact_name} · ` : ""}{modal.contact_phone}
                          </div>
                          <textarea
                            value={waMessage} onChange={e => setWaMessage(e.target.value)}
                            placeholder="Type your WhatsApp message…"
                            rows={3}
                            style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #dcfce7", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", marginBottom: 10, boxSizing: "border-box", background: "#f0fdf4" }}
                          />
                          <button onClick={handleSendWhatsApp} disabled={waSending || !waMessage.trim()}
                            style={{ width: "100%", padding: "10px", background: waMessage.trim() ? "#15803d" : "#e2e8f0",
                              color: waMessage.trim() ? "#fff" : "#94a3b8", border: "none", borderRadius: 10,
                              fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: waMessage.trim() ? "pointer" : "not-allowed" }}>
                            {waSending ? "Sending…" : "💬 Send WhatsApp"}
                          </button>
                          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6, textAlign: "center" }}>
                            Message is sent via WhatsApp Business API and logged to the timeline
                          </div>
                        </>
                      ) : (
                        <div style={{ background: "#fef9c3", borderRadius: 10, padding: 12, fontSize: 12, color: "#92400e", textAlign: "center" }}>
                          ⚠️ No phone number saved for this lead.<br />
                          <span style={{ fontSize: 11 }}>Add contact phone in the Notes tab to enable WhatsApp.</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <input
                        value={logTitle} onChange={e => setLogTitle(e.target.value)}
                        placeholder={logType === "call_log" ? "e.g. Called Principal, discussed pricing" : logType === "email_out" ? "e.g. Sent proposal via email" : "e.g. Decision postponed to next month"}
                        style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", marginBottom: 8, boxSizing: "border-box" }}
                      />
                      <textarea
                        value={logBody} onChange={e => setLogBody(e.target.value)}
                        placeholder="Additional details (optional)"
                        rows={2}
                        style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", marginBottom: 10, boxSizing: "border-box" }}
                      />
                      <button onClick={handleLogActivity} disabled={logging || !logTitle.trim()}
                        style={{ width: "100%", padding: "10px", background: logTitle.trim() ? accent : "#e2e8f0",
                          color: logTitle.trim() ? "#fff" : "#94a3b8", border: "none", borderRadius: 10,
                          fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: logTitle.trim() ? "pointer" : "not-allowed" }}>
                        {logging ? "Saving…" : "Log Activity"}
                      </button>
                    </>
                  )}
                </div>

                {/* Timeline feed */}
                {activitiesLoading ? (
                  <div style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>Loading timeline…</div>
                ) : activities.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
                    <div style={{ fontWeight: 600 }}>No activity yet</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Visits, follow-ups and stage changes will appear here</div>
                  </div>
                ) : (
                  <div style={{ position: "relative", paddingLeft: 28 }}>
                    {/* Vertical line */}
                    <div style={{ position: "absolute", left: 10, top: 8, bottom: 8, width: 2, background: "#e2e8f0", borderRadius: 2 }} />

                    {activities.map((act, i) => {
                      const cfg = ACT[act.type] || { icon: "•", label: act.type, color: "#64748b", bg: "#f1f5f9" };
                      const dt  = new Date(act.created_at);
                      const dateStr = dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
                      const timeStr = dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });

                      return (
                        <div key={act.id} style={{ position: "relative", marginBottom: 18 }}>
                          {/* Dot */}
                          <div style={{ position: "absolute", left: -22, top: 2, width: 22, height: 22, borderRadius: "50%",
                            background: cfg.bg, border: `2px solid ${cfg.color}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, zIndex: 1 }}>
                            {cfg.icon}
                          </div>

                          {/* Card */}
                          <div style={{ background: "#fff", border: "1.5px solid #f1f5f9", borderRadius: 12, padding: "10px 12px" }}>
                            {/* Type badge + time */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                              <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 6, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>
                                {cfg.label}
                              </span>
                              <span style={{ fontSize: 10, color: "#94a3b8" }}>{dateStr} · {timeStr}</span>
                            </div>

                            {/* Title */}
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: act.body ? 4 : 0 }}>
                              {act.title}
                            </div>

                            {/* Body */}
                            {act.body && (
                              <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>{act.body}</div>
                            )}

                            {/* Logged by */}
                            <div style={{ fontSize: 10, color: "#cbd5e1", marginTop: 5 }}>
                              by {act.user_name}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/*  ADVANCE STAGE VIEW  */}
            {view === "advance" && (
              <div>
                {nextStages(modal.pipeline_stage).length === 0 ? (
                  <div style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}></div>
                    <div style={{ fontWeight: 700 }}>Lead is fully advanced</div>
                    <div style={{ fontSize: 13 }}>This lead has reached the final stage</div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 10, textTransform: "uppercase" }}>Advance To</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                      {nextStages(modal.pipeline_stage).map(s => {
                        const cfg = PIPELINE_MAP[s.key] || {};
                        return (
                          <button key={s.key} onClick={() => setTargetStage(s.key)}
                            style={{ padding: "12px 16px", borderRadius: 12, border: `2px solid ${targetStage === s.key ? (cfg.color || accent) : "#e2e8f0"}`,
                              background: targetStage === s.key ? (cfg.bg || "#f1f5f9") : "#fff",
                              cursor: "pointer", fontFamily: "inherit", textAlign: "left", display: "flex", alignItems: "center", gap: 12 }}>
                            <span style={{ fontSize: 20 }}>{s.icon}</span>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: targetStage === s.key ? (cfg.color || "#1e293b") : "#1e293b" }}>{s.label}</div>
                              {s.key === "closed_won" && (
                                <div style={{ fontSize: 11, color: "#64748b" }}>Payment received  deal complete!</div>
                              )}
                              {s.key === "proposal_sent" && (
                                <div style={{ fontSize: 11, color: "#64748b" }}>Proposal/Quotation shared with institution</div>
                              )}
                              {s.key === "mou_shared" && (
                                <div style={{ fontSize: 11, color: "#64748b" }}>MOU document sent for review</div>
                              )}
                              {s.key === "mou_signed" && (
                                <div style={{ fontSize: 11, color: "#64748b" }}>MOU signed by both parties</div>
                              )}
                              {s.key === "payment_requested" && (
                                <div style={{ fontSize: 11, color: "#64748b" }}>Payment request sent to institution</div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Deal value (shown for closed_won) */}
                    {(targetStage === "closed_won" || targetStage === "payment_requested") && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>
                          Deal Value (Rs.) {targetStage === "closed_won" ? " Required" : ""}
                        </div>
                        <input type="number" value={dealValue} onChange={e => setDealValue(e.target.value)}
                          placeholder="e.g. 150000"
                          style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                      </div>
                    )}

                    {!targetStage.startsWith("closed_") && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>Next Action *</div>
                        <input value={nextAction} onChange={e => setNextAction(e.target.value)} placeholder="e.g. Send revised proposal and call principal"
                          style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                      </div>
                    )}

                    {["proposal_sent", "mou_shared", "mou_signed", "payment_requested"].includes(targetStage) && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>Expected Close Date *</div>
                        <input type="date" min={new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })} value={expectedCloseDate} onChange={e => setExpectedCloseDate(e.target.value)}
                          style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                      </div>
                    )}

                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>Notes (optional)</div>
                      <textarea value={stageNotes} onChange={e => setStageNotes(e.target.value)}
                        placeholder="Any remarks about this stage"
                        style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical", minHeight: 70, outline: "none", boxSizing: "border-box" }} />
                    </div>

                    <button onClick={handleAdvance} disabled={advancing || !targetStage}
                      style={{ width: "100%", padding: 14, background: targetStage ? accent : "#e2e8f0", color: targetStage ? "#fff" : "#94a3b8",
                        border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: targetStage ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                      {advancing ? "Advancing" : `Advance to ${PIPELINE_MAP[targetStage]?.label || "Selected Stage"}`}
                    </button>
                  </>
                )}
              </div>
            )}

            {/*  NOTES & TEMPERATURE VIEW  */}
            {view === "notes" && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 10, textTransform: "uppercase" }}>Lead Temperature</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  {Object.entries(TEMP).map(([k, t]) => (
                    <button key={k} onClick={() => setTempUpdate(k)}
                      style={{ flex: 1, padding: "10px 6px", border: `2px solid ${tempUpdate === k ? t.color : "#e2e8f0"}`,
                        borderRadius: 10, background: tempUpdate === k ? t.bg : "#fff", cursor: "pointer", fontFamily: "inherit" }}>
                      <div style={{ fontSize: 20 }}>{t.icon}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: tempUpdate === k ? t.color : "#94a3b8" }}>{t.label}</div>
                    </button>
                  ))}
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>Notes</div>
                  <textarea value={notesUpdate} onChange={e => setNotesUpdate(e.target.value)}
                    placeholder="Add notes about this lead"
                    style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical", minHeight: 100, outline: "none", boxSizing: "border-box" }} />
                </div>

                <button onClick={handleSaveNotes} disabled={saving}
                  style={{ width: "100%", padding: 14, background: accent, color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {saving ? "Saving" : "Save Changes"}
                </button>
              </div>
            )}

            {view === "reactivate" && (
              <div>
                <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:12, padding:14, marginBottom:14 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:"#166534" }}>Reactivate this lead</div>
                  <div style={{ fontSize:12, color:"#166534", marginTop:4 }}>The original loss reason and history will remain preserved.</div>
                  {modal.lost_reason_category && <div style={{ fontSize:11, color:"#64748b", marginTop:7 }}>Previously closed: {modal.lost_reason_category.replaceAll("_"," ")}{modal.competitor_name ? ` · ${modal.competitor_name}` : ""}</div>}
                </div>
                <div style={{ marginBottom:12 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#64748b", marginBottom:7, textTransform:"uppercase" }}>New Next Action *</div>
                  <input value={reactivateAction} onChange={e => setReactivateAction(e.target.value)} placeholder="e.g. Call principal about new programme" style={{ width:"100%", boxSizing:"border-box", padding:"11px 14px", border:"1.5px solid #e2e8f0", borderRadius:10, fontFamily:"inherit" }} />
                </div>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#64748b", marginBottom:7, textTransform:"uppercase" }}>Follow-up Date *</div>
                  <input type="date" min={new Date().toLocaleDateString("en-CA", { timeZone:"Asia/Kolkata" })} value={reactivateDate} onChange={e => setReactivateDate(e.target.value)} style={{ width:"100%", boxSizing:"border-box", padding:"11px 14px", border:"1.5px solid #e2e8f0", borderRadius:10, fontFamily:"inherit" }} />
                </div>
                <button onClick={handleReactivate} disabled={reactivating || !reactivateAction.trim() || !reactivateDate} style={{ width:"100%", padding:14, border:"none", borderRadius:12, background:reactivateAction.trim()&&reactivateDate?"#15803d":"#e2e8f0", color:reactivateAction.trim()&&reactivateDate?"#fff":"#94a3b8", fontFamily:"inherit", fontWeight:800, cursor:"pointer" }}>{reactivating ? "Reactivating..." : "Reactivate & Schedule Follow-up"}</button>
              </div>
            )}

            {/*  CLOSE LEAD VIEW  */}
            {view === "close" && (
              <div>
                <div style={{ background: "#fff5f5", border: "1.5px solid #fecaca", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#dc2626", marginBottom: 6 }}> Close as Not Interested</div>
                  <div style={{ fontSize: 13, color: "#7f1d1d", lineHeight: 1.5 }}>
                    This will remove the lead from your active pipeline. The record will be preserved in the system for future product introductions.
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>
                    Reason for Closing <span style={{ color: "#dc2626" }}>*</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap: 8, marginBottom: 10 }}>
                    {LOST_REASONS.map(([key, label]) => (
                      <button key={key} onClick={() => setLostReasonCategory(key)}
                        style={{ padding: "10px 10px", borderRadius: 10, border: `2px solid ${lostReasonCategory === key ? "#dc2626" : "#e2e8f0"}`,
                          background: lostReasonCategory === key ? "#fee2e2" : "#fff",
                          cursor: "pointer", fontFamily: "inherit", textAlign: "left", fontSize: 13, fontWeight: 600,
                          color: lostReasonCategory === key ? "#dc2626" : "#374151" }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {lostReasonCategory === "competitor_selected" && <input value={competitorName} onChange={e => setCompetitorName(e.target.value)} placeholder="Competitor name *" maxLength={150} style={{ width:"100%", boxSizing:"border-box", padding:"11px 14px", border:"1.5px solid #e2e8f0", borderRadius:10, marginBottom:10, fontFamily:"inherit" }} />}
                  <textarea value={closedReason} onChange={e => setClosedReason(e.target.value)}
                    placeholder="Required remarks: what happened and what may help future reactivation?"
                    style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical", minHeight: 70, outline: "none", boxSizing: "border-box" }} />
                </div>

                <button onClick={handleClose} disabled={closing || !lostReasonCategory || !closedReason.trim()}
                  style={{ width: "100%", padding: 14, background: lostReasonCategory && closedReason.trim() ? "#dc2626" : "#e2e8f0",
                    color: lostReasonCategory && closedReason.trim() ? "#fff" : "#94a3b8",
                    border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700,
                    cursor: lostReasonCategory && closedReason.trim() ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                  {closing ? "Closing" : " Close Lead as Not Interested"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ═══════════════════════════════════════════════════════
          CALL LOG DIALOG — appears after tapping "Call" button
      ═══════════════════════════════════════════════════════ */}
      {callDialog && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 300, display: "flex", alignItems: "flex-end" }}
          onClick={e => { if (e.target === e.currentTarget) setCallDialog(null); }}>
          <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", boxSizing: "border-box" }}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>📞 Log This Call</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{callDialog.institution_name}</div>
              </div>
              <button onClick={() => setCallDialog(null)}
                style={{ background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16 }}>✕</button>
            </div>

            {/* Outcome selector */}
            <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>Call Outcome</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
              {CALL_OUTCOME_OPTIONS.map(o => (
                <button key={o.key} onClick={() => setCallOutcome(o.key)}
                  style={{ padding: "8px 12px", borderRadius: 10, border: `2px solid ${callOutcome === o.key ? o.color : "#e2e8f0"}`,
                    background: callOutcome === o.key ? o.bg : "#fff",
                    cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 12,
                    color: callOutcome === o.key ? o.color : "#94a3b8" }}>
                  {o.label}
                </button>
              ))}
            </div>

            {/* Duration (shown only for picked calls) */}
            {callOutcome === "call_picked" && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>Duration (minutes)</div>
                <input type="number" value={callDuration} onChange={e => setCallDuration(e.target.value)}
                  placeholder="e.g. 5"
                  style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
              </div>
            )}

            {/* Notes */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>Call Notes (optional)</div>
              <textarea value={callNotes} onChange={e => setCallNotes(e.target.value)}
                placeholder="e.g. Discussed pricing, follow up next week"
                rows={3}
                style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            </div>

            <button onClick={() => setCallDialog(null)}
              style={{ width: "100%", padding: 13, background: "#f1f5f9", color: "#64748b",
                border: "none", borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
              Cancel
            </button>

            <button onClick={handleLogCall} disabled={loggingCall}
              style={{ width: "100%", padding: 14, background: "#0e7490", color: "#fff",
                border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {loggingCall ? "Saving…" : "Log Call to CRM"}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          WHATSAPP LOG DIALOG — after opening wa.me link
      ═══════════════════════════════════════════════════════ */}
      {waLogDialog && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 300, display: "flex", alignItems: "flex-end" }}
          onClick={e => { if (e.target === e.currentTarget) setWaLogDialog(null); }}>
          <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", boxSizing: "border-box" }}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#166534" }}>💬 Log WhatsApp Message</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  Sent to {waLogDialog.contact_name || waLogDialog.institution_name} · {waLogDialog.contact_phone}
                </div>
              </div>
              <button onClick={() => setWaLogDialog(null)}
                style={{ background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16 }}>✕</button>
            </div>

            <div style={{ background: "#f0fdf4", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#166534" }}>
              ✅ WhatsApp opened. Record what you sent below so it's saved in the CRM timeline.
            </div>

            <button onClick={() => setShowTemplatePicker("whatsapp")}
              style={{ marginBottom: 12, padding: "9px 14px", background: "#dcfce7", color: "#166534",
                border: "1px solid #86efac", borderRadius: 10, fontSize: 12, fontWeight: 800,
                cursor: "pointer", fontFamily: "inherit" }}>
              💬 Use WhatsApp Template
            </button>

            <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>What did you send?</div>
            <textarea value={waLogMessage} onChange={e => setWaLogMessage(e.target.value)}
              placeholder="e.g. Shared our product brochure and pricing details"
              rows={4}
              style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #86efac", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", marginBottom: 16, boxSizing: "border-box", background: "#f0fdf4" }} />

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setWaLogDialog(null)}
                style={{ flex: 1, padding: 13, background: "#f1f5f9", color: "#64748b",
                  border: "none", borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                Skip
              </button>
              <button onClick={handleLogWaMessage} disabled={loggingWa || !waLogMessage.trim()}
                style={{ flex: 2, padding: 13, background: waLogMessage.trim() ? "#166534" : "#e2e8f0",
                  color: waLogMessage.trim() ? "#fff" : "#94a3b8",
                  border: "none", borderRadius: 12, fontSize: 13, fontWeight: 700,
                  cursor: waLogMessage.trim() ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                {loggingWa ? "Saving…" : "💬 Log to CRM"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          EMAIL COMPOSE DIALOG — send via Yahoo SMTP
      ═══════════════════════════════════════════════════════ */}
      {emailDialog && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 300, display: "flex", alignItems: "flex-end" }}
          onClick={e => { if (e.target === e.currentTarget) setEmailDialog(null); }}>
          <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxHeight: "90vh", overflowY: "auto", boxSizing: "border-box" }}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#c2410c" }}>📧 Send Email</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{emailDialog.institution_name}</div>
              </div>
              <button onClick={() => setEmailDialog(null)}
                style={{ background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16 }}>✕</button>
            </div>

            <button onClick={() => setShowTemplatePicker("email")}
              style={{ marginBottom: 14, padding: "9px 14px", background: "#e0f2fe", color: "#0369a1",
                border: "1px solid #bae6fd", borderRadius: 10, fontSize: 12, fontWeight: 800,
                cursor: "pointer", fontFamily: "inherit" }}>
              📝 Use Email Template
            </button>

            {/* To */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6, textTransform: "uppercase" }}>To</div>
              <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)}
                placeholder="contact@institution.edu"
                style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
            </div>

            {/* Subject */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6, textTransform: "uppercase" }}>Subject</div>
              <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                placeholder="Subject"
                style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6, textTransform: "uppercase" }}>Status Update</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {EMAIL_STATUS_OPTIONS.map(opt => {
                  const sel = emailStatus === opt.key;
                  return (
                    <button key={opt.key} onClick={() => setEmailStatus(opt.key)}
                      style={{ padding: "8px 12px", borderRadius: 10, border: `1.5px solid ${sel ? "#c2410c" : "#e2e8f0"}`, background: sel ? "#ffedd5" : "#fff", color: sel ? "#c2410c" : "#64748b", fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {emailStatus === "others" && (
                <input value={emailStatusOther} onChange={e => setEmailStatusOther(e.target.value)}
                  placeholder="Type email status"
                  style={{ width: "100%", marginTop: 8, padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
              )}
            </div>

            {/* Body */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6, textTransform: "uppercase" }}>Message</div>
              <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)}
                rows={8}
                style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.6 }} />
            </div>

            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 14, textAlign: "center" }}>
              Sent from your official company email · automatically logged to this lead's timeline
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setEmailDialog(null)}
                style={{ flex: 1, padding: 13, background: "#f1f5f9", color: "#64748b",
                  border: "none", borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={handleSendEmail} disabled={sendingEmail || !emailTo.trim() || !emailSubject.trim() || !emailBody.trim()}
                style={{ flex: 2, padding: 13,
                  background: (emailTo.trim() && emailSubject.trim() && emailBody.trim()) ? "#c2410c" : "#e2e8f0",
                  color: (emailTo.trim() && emailSubject.trim() && emailBody.trim()) ? "#fff" : "#94a3b8",
                  border: "none", borderRadius: 12, fontSize: 13, fontWeight: 700,
                  cursor: (emailTo.trim() && emailSubject.trim()) ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                {sendingEmail ? "Sending…" : "📧 Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTemplatePicker && templateLead && (
        <TemplatePicker
          type={showTemplatePicker}
          accent={accent}
          vars={{
            contact_name: templateLead.contact_name || "",
            institution_name: templateLead.institution_name || "",
            sender_name: templateSender,
            company_name: templateCompany,
          }}
          onSelect={({ subject, body }) => {
            if (showTemplatePicker === "email") {
              if (subject) setEmailSubject(subject);
              setEmailBody(body || "");
              toast.success("Email template applied");
            } else {
              setWaLogMessage(body || "");
              toast.success("WhatsApp template applied");
            }
          }}
          onClose={() => setShowTemplatePicker(null)}
        />
      )}

    </div>
  );
}
